/**
 * BankFeedTransactionsTab - Phase 2 cont. (final).
 * Extracted Transactions tab body (Review Needed / Completed / Duplicates /
 * Cross-Bank / Failed sub-filters). To keep the diff faithful and avoid
 * touching 70+ inline references we accept a single `bf` props bundle and
 * destructure once at the top.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { API } from '../../App';
import { showPeriodLockToast } from '../../utils/periodLockToast';
import {
  Clock, CheckCircle2, Ban, GitCompare, AlertCircle, Activity, Link2,
  Sparkles, BookOpen, FileText, Undo2, Scissors, Eye, ArrowDownRight,
  ArrowLeftRight, Check, Zap, RefreshCw, Edit3, ChevronLeft, ChevronRight,
  Hourglass,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { TransactionTable } from './TransactionTable';
import { TransactionTableHeader } from './TransactionTableHeader';
import { BulkActionsToolbar } from './BulkActionsToolbar';
import { BulkAttachDocsDialog } from '../documents/BulkAttachDocsDialog';
import { ChannelChip } from './ChannelChip';
import { deriveChannel } from '../../utils/bankChannel';
import { PendingTransactionCard } from './PendingTransactionCard';
import { CompletedTransactionCard } from './CompletedTransactionCard';
import { formatCurrency } from '../../utils/currency';
import { humanizeReviewReason } from './reviewReason';
import { InfoTip } from '../ui/info-tip';

const PAGE_SIZE = 50;

export const BankFeedTransactionsTab = ({ bf }) => {
  const navigate = useNavigate();
  // Destructure the entire bundle so the JSX below stays identical to the
  // original inline version. New props get added here; everything else is
  // already wired by the parent.
  const {
    // filters / chips
    txFilter, setTxFilter, stats, potentialDuplicates,
    // iter971k-C P1 - chip candidate counts
    pendingVatCandidates = 0, pendingWhtCandidates = 0, pendingPotentialInputVat = 0,
    processedVatCandidates = 0, processedWhtCandidates = 0, processedPotentialInputVat = 0,
    // pending data + selection
    filteredPendingItems, filteredProcessedItems, duplicateItems, failedItems,
    selectedItems, bulkProcessing, isApproving, autoApproveOpen, setAutoApproveOpen,
    // currency / accounts / config
    selectedBankCurrency, availableGlAccounts, bankAccounts, business, taxCtx, taxStatus, tour,
    // filters (legacy)
    tableFilters, setTableFilters, searchTerm, setSearchTerm,
    filterDirection, setFilterDirection, filterBankAccountId, setFilterBankAccountId,
    filterSource, setFilterSource,
    setFilterGlCode, setFilterStartDate, setFilterEndDate,
    filterGlCode, filterStartDate, filterEndDate,
    txViewMode, setTxViewMode,
    // pagination
    pendingPage, pendingTotalPages, pendingTotalCount, setPendingPage, fetchPendingItems,
    processedPage, processedTotalPages, processedTotalCount, setProcessedPage, fetchProcessedItems,
    // iter1107 - awaiting bank bucket (visible-but-inert)
    awaitingBankItems = [], awaitingBankTotalCount = 0,
    // selection handlers
    toggleSelectAll, clearSelection, toggleItemSelection,
    // bulk
    initiateBulkAction, openBulkReclassifyDialog, openBulkCategoryDialog, openBulkUndoDialog,
    // single-row actions
    handleQuickApprove, openReclassifyDialog, openSplitDialog, openTaxPaymentDialog,
    handleInlineGlChange, handleInlineDescriptionChange, handleInlineCounterpartyChange, handleUnlinkTx, openLinkDialogForTx,
    pendingGlEdits,    // Phase #5: staged category edits Map
    handleSaveRow,     // Phase 1.3: per-row Save Changes for Completed tab
    handleRequestAiSuggestion, // Phase 2.1. AI re-classify on description edit
    handleApplyAiSuggestion,   // Phase 2.1: apply AI suggestion
    handleToggleVat, handleToggleWht, togglingTax, fetchTaxStatus,
    openUndoClassificationDialog, navigateToDocument,
    // iter1404 Phase 4b · open the In Books drill (per-tx journal + linked docs)
    openInBooks,
    // dialogs
    setSelectedItem, setShowDetailDialog,
    // duplicates / failed / cross-bank
    handleProcessDuplicateAnyway, handleDismissDuplicate,
    handleResolvePotentialDuplicate, processingId, handleRetryFailed,
    openManualProcessDialog, handleDismissFailed,
    // formatters
    isNewTransaction, formatAmount, getSourceBadge, getBankLabel,
    getDirectionIcon, getStatusBadge,
  } = bf;

  // In Books: compact cards on phones (<768px), table on larger screens.
  const [isPhone, setIsPhone] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsPhone(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // P2 · every row renders in ITS bank account's currency (rows synced from Plaid/Mono carry no currency)
  const rowCcy = (item) => (item?.currency || item?.bank_currency
    || (bankAccounts || []).find((b) => b.id === item?.bank_account_id)?.currency
    || selectedBankCurrency || business?.currency || 'USD');

  // iter 740 P0-C - one-click "Make this a rule" from any classified row.
  // AI keeps being the safety net; this just promotes a successful AI
  // classification (or a user re-classification) into a deterministic rule.
  const handleMakeRule = React.useCallback(async (tx) => {
    try {
      const resp = await axios.post(
        `${API}/ai-corrections/training-examples/from-transaction`,
        { transaction_id: tx.id, amount_band_pct: 20.0, scope: 'bank_feed' },
      );
      if (resp.data?.success) {
        if (resp.data.created) {
          toast.success(`Rule created: future ${tx.counterparty || 'similar'} transactions will skip AI.`);
        } else {
          toast.info(resp.data.message || 'You already have a rule for this pattern.');
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create rule from this transaction.');
    }
  }, []);

  // Inline project allocation - saved immediately via save-changes.
  // Pending rows: stamps the staging row (inherited when posted).
  // Completed rows: re-stamps the posted journal + linked document.
  const handleProjectChange = React.useCallback(async (tx, projectId) => {
    try {
      const payload = projectId ? { project_id: projectId } : { clear_project: true };
      await axios.post(`${API}/bank-feeds/save-changes/${tx.id}`, payload);
      toast.success(projectId ? 'Project assigned' : 'Project cleared');
      if (tx.status === 'completed') await fetchProcessedItems(processedPage);
      else await fetchPendingItems(pendingPage);
    } catch (e) {
      if (showPeriodLockToast(e, navigate)) {
        // period-lock toast handled
      } else if (e?.response?.status === 423) {
        toast.error(e?.response?.data?.detail || 'Period is locked: unlock it to re-tag this transaction.');
      } else {
        toast.error(e?.response?.data?.detail || 'Could not update project');
      }
    }
  }, [fetchPendingItems, fetchProcessedItems, pendingPage, processedPage, navigate]);

  // iter1086h - channel filter (Open Banking / Statement Upload / Manual).
  // Local UI-only state; parent filters remain untouched.
  const [channelFilter, setChannelFilter] = React.useState('all');
  const _matchesChannelFilter = React.useCallback((item) => {
    if (channelFilter === 'all') return true;
    return (item.channel || deriveChannel(item.source)) === channelFilter;
  }, [channelFilter]);

  // Feb 2026 - bulk attach doc across all selected rows.
  const [bulkAttachOpen, setBulkAttachOpen] = React.useState(false);
  const openBulkAttachDialog = React.useCallback(() => {
    if (!selectedItems || selectedItems.size === 0) {
      toast.info('Select at least one transaction first.');
      return;
    }
    setBulkAttachOpen(true);
  }, [selectedItems]);
  const selectedIdsArray = React.useMemo(
    () => (selectedItems ? Array.from(selectedItems) : []),
    [selectedItems]
  );

  // Feb 2026 - stable callbacks passed to cards. Recreating these inline as
  // arrow functions inside a `.map()` caused every parent re-render to hand
  // each card fresh callback references, which combined with the fresh
  // `taxToggle` object below cascaded into an infinite update loop the
  // moment the Cards view was mounted with a bank filter (>~40 cards).
  const handleAfterPendingRefresh = React.useCallback(
    async () => { await fetchPendingItems(pendingPage); },
    [fetchPendingItems, pendingPage],
  );
  const handleAfterProcessedRefresh = React.useCallback(
    async () => { await fetchProcessedItems(processedPage); },
    [fetchProcessedItems, processedPage],
  );
  const handleViewDetails = React.useCallback(
    (tx) => { setSelectedItem(tx); setShowDetailDialog(true); },
    [setSelectedItem, setShowDetailDialog],
  );
  const handleNavigateToDocument = React.useCallback(
    (tx) => navigateToDocument(tx.document_type, tx.document_id),
    [navigateToDocument],
  );

  return (
    <>
            {/* Sub-filter chips - clearer active state with solid bg + check icon.
                Review-Needed + Completed are ALWAYS shown (the two primary views).
                Duplicates / Cross-Bank / Failed are "issue" chips: they only
                surface when their count is > 0, keeping the toolbar clean for
                the majority of users who never hit these states. The active
                filter chip stays pinned even at count==0 so a user who is
                currently inspecting an empty Issues view never sees the chip
                disappear under them. */}
            <div className="flex flex-wrap items-center gap-2" data-testid="tx-filter-chips">
              {[
                { key: 'pending',        label: 'Review Needed',        icon: Clock,        count: stats.review_needed,         activeBg: 'bg-amber-600',   activeRing: 'ring-amber-300',  pulse: stats.review_needed > 0, alwaysShow: true },
                { key: 'processed',      label: 'In Books',             icon: CheckCircle2, count: stats.completed,             activeBg: 'bg-emerald-600', activeRing: 'ring-emerald-300', pulse: false, alwaysShow: true },
                { key: 'awaiting_bank',  label: 'Awaiting Bank Post',   icon: Hourglass,    count: stats.awaiting_bank || 0,    activeBg: 'bg-sky-600',     activeRing: 'ring-sky-300',    pulse: false, alwaysShow: false },
                { key: 'duplicates',     label: 'Duplicates',           icon: Ban,          count: stats.duplicates,            activeBg: 'bg-slate-700',   activeRing: 'ring-slate-300',  pulse: false, alwaysShow: false },
                { key: 'cross-bank',     label: 'Possible Duplicates',  icon: GitCompare,   count: potentialDuplicates.length,  activeBg: 'bg-amber-700',   activeRing: 'ring-amber-300',  pulse: true,  alwaysShow: false },
                { key: 'failed',         label: 'Failed',               icon: AlertCircle,  count: stats.failed,                activeBg: 'bg-red-600',     activeRing: 'ring-red-300',    pulse: stats.failed > 0, alwaysShow: false },
              ]
              .filter(({ key, count, alwaysShow }) => alwaysShow || count > 0 || txFilter === key)
              .map(({ key, label, icon: Icon, count, activeBg, activeRing, pulse }) => {
                const isActive = txFilter === key;
                return (
                  <button
                    key={key}
                    onClick={() => setTxFilter(key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      isActive
                        ? `${activeBg} text-white border-transparent shadow-sm ring-2 ring-offset-1 ${activeRing}`
                        : `border-slate-200 text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-300 ${pulse && !isActive ? 'ring-1 ring-amber-300/60' : ''}`
                    }`}
                    data-testid={`tx-filter-${key}`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : ''}`} />
                    {label}
                    {count > 0 && (
                      <span className={`ml-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* iter1086h - channel filter (Open Banking / Statement / Manual).
                Only rendered when the account actually has BOTH open banking
                and statement data - single-channel accounts don't need this
                affordance. Detection is by scanning the currently visible
                filtered rows. */}
            {(() => {
              const _visible = [...(filteredPendingItems || []), ...(filteredProcessedItems || [])];
              const _channels = new Set(_visible.map(i => i.channel || deriveChannel(i.source)));
              if (_channels.size < 2) return null;
              const _CHIPS = [
                { key: 'all',              label: 'All channels',   tone: 'bg-white text-slate-700 border-slate-200' },
                { key: 'open_banking',     label: 'Bank Sync',   tone: 'bg-[#108981]/8 text-[#108981] border-[#108981]/30' },
                { key: 'statement_upload', label: 'Statement Upload', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
                { key: 'manual_entry',     label: 'Manual Entry',   tone: 'bg-slate-100 text-slate-700 border-slate-300' },
              ].filter(c => c.key === 'all' || _channels.has(c.key));
              // Show a discoverable "Reconcile channels" CTA when the user
              // is looking at a specific bank that has both channels.
              const canReconcile = _channels.has('open_banking') && _channels.has('statement_upload')
                && filterBankAccountId && filterBankAccountId !== 'all';
              return (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2" data-testid="channel-filter-chips">
                    <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Channel</span>
                    {_CHIPS.map(c => {
                      const isActive = channelFilter === c.key;
                      return (
                        <button
                          key={c.key}
                          type="button"
                          onClick={() => setChannelFilter(c.key)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${isActive ? 'ring-2 ring-offset-1 ring-slate-400 ' + c.tone : c.tone + ' opacity-70 hover:opacity-100'}`}
                          data-testid={`channel-filter-${c.key}`}
                        >
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                  {canReconcile && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 flex items-center justify-between gap-3" data-testid="channel-recon-cta">
                      <p className="text-xs text-amber-900 flex-1">
                        <span className="font-semibold">This bank has activity from BOTH channels.</span>{' '}
                        Open the cutover reconciliation view to compare closing balances and resolve unmatched rows.
                      </p>
                      <a
                        href={`/bank-accounts/${filterBankAccountId}/channel-reconciliation`}
                        className="text-xs font-semibold text-amber-900 hover:text-amber-700 underline whitespace-nowrap"
                      >
                        Open reconciliation →
                      </a>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Pending Review Content */}
            {txFilter === 'pending' && (
              <div className="space-y-4" data-testid="pending-content">
            {/* Phase B - Smart shortcuts: one-click auto-approve + quick-start tour */}
            {(() => {
              const aiAutoThreshold = 0.80;
              const highConfidence = filteredPendingItems.filter(t => (t.ai_confidence ?? t.confidence ?? 0) >= aiAutoThreshold);
              if (highConfidence.length === 0 && (!tour || tour?.progress?.['bank-feed-quickstart']?.completed)) return null;
              return (
                <div className="flex flex-wrap items-center gap-2" data-testid="bf-smart-shortcuts">
                  {highConfidence.length > 0 && (
                    <Button
                      size="sm"
                      onClick={() => setAutoApproveOpen(true)}
                      className="h-8 bg-violet-600 hover:bg-violet-700 text-white text-xs"
                      data-testid="bf-auto-approve-cta"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                      Auto-approve {highConfidence.length} high-confidence
                    </Button>
                  )}
                  {tour && !tour?.progress?.['bank-feed-quickstart']?.completed && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Use restartTour so the button works even after a
                        // previous dismiss/complete. startTour silently no-ops
                        // if dismissed/completed, which surfaced as "the button
                        // does nothing" UX. See TourProvider.jsx::startTour.
                        if (tour?.restartTour) tour.restartTour('bank-feed-quickstart');
                        else tour?.startTour?.('bank-feed-quickstart');
                      }}
                      className="h-8 text-xs"
                      data-testid="bf-quickstart-cta"
                    >
                      <BookOpen className="w-3.5 h-3.5 mr-1.5" />
                      Show me how (60s tour)
                    </Button>
                  )}
                </div>
              );
            })()}
            {/* Phase A - Comprehensive table header (KPIs + filters + bulk + tax chips) */}
            <TransactionTableHeader
              mode="pending"
              transactions={filteredPendingItems}
              currency={selectedBankCurrency || 'NGN'}
              selectedCount={selectedItems.size}
              totalCount={filteredPendingItems.length}
              isAllSelected={selectedItems.size === filteredPendingItems.length && filteredPendingItems.length > 0}
              onToggleSelectAll={toggleSelectAll}
              onClearSelection={clearSelection}
              onBulkApprove={() => initiateBulkAction('approve')}
              onBulkReclassify={openBulkReclassifyDialog}
              filters={{
                ...tableFilters,
                search: searchTerm,
                direction: filterDirection,
                bankId: filterBankAccountId,
                source: filterSource,
                glCode: filterGlCode,
                startDate: filterStartDate,
                endDate: filterEndDate,
              }}
              onFiltersChange={(next) => {
                setTableFilters(next);
                if ('search' in next) setSearchTerm(next.search || '');
                if ('direction' in next) setFilterDirection(next.direction || 'all');
                if ('bankId' in next) setFilterBankAccountId(next.bankId || 'all');
                if ('source' in next) setFilterSource(next.source || 'all');
                if ('glCode' in next) setFilterGlCode(next.glCode || 'all');
                if ('startDate' in next) setFilterStartDate(next.startDate || '');
                if ('endDate' in next) setFilterEndDate(next.endDate || '');
              }}
              bankAccounts={bankAccounts}
              glAccounts={availableGlAccounts}
              viewMode={txViewMode}
              onViewModeChange={setTxViewMode}
              taxCtx={taxCtx}
              vatCandidates={pendingVatCandidates}
              whtCandidates={pendingWhtCandidates}
              potentialInputVat={pendingPotentialInputVat}
            />

            {/* Bulk actions toolbar - shows when items are selected in EITHER table or cards view */}
            {selectedItems.size > 0 && (
              <BulkActionsToolbar
                selectedCount={selectedItems.size}
                totalCount={filteredPendingItems.length}
                isAllSelected={selectedItems.size === filteredPendingItems.length && filteredPendingItems.length > 0}
                isProcessing={bulkProcessing}
                onToggleSelectAll={toggleSelectAll}
                onClearSelection={clearSelection}
                onBulkApprove={() => initiateBulkAction('approve')}
                onBulkReclassify={openBulkCategoryDialog}
                onBulkAttachDocs={openBulkAttachDialog}
                onBulkMatch={() => {
                  const rows = filteredPendingItems.filter(t => selectedItems.has(t.id));
                  const dirs = new Set(rows.map(r => r.direction || 'outflow'));
                  if (dirs.size !== 1) { toast.error('Select bank lines of the same direction (all money in, or all money out).'); return; }
                  openLinkDialogForTx(rows);
                }}
              />
            )}
            {filteredPendingItems.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle2 className="w-16 h-16 text-[#108981] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">All Caught Up!</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {searchTerm || filterDirection !== 'all' 
                      ? 'No transactions match your filters.' 
                      : 'No transactions need your review. Upload a bank statement to get started.'}
                  </p>
                </CardContent>
              </Card>
            ) : txViewMode === 'table' ? (
              <TransactionTable
                mode="pending"
                transactions={filteredPendingItems}
                currency={selectedBankCurrency || 'NGN'}
                baseCurrency={business?.currency || null}
                glAccounts={availableGlAccounts || []}
                bankAccounts={bankAccounts || []}
                taxCtx={taxCtx}
                selectedIds={selectedItems}
                onToggleSelect={toggleItemSelection}
                onToggleSelectAll={toggleSelectAll}
                onView={(tx) => { setSelectedItem(tx); setShowDetailDialog(true); }}
                onApprove={handleQuickApprove}
                onReclassify={openReclassifyDialog}
                onSplit={openSplitDialog}
                onTaxPayment={openTaxPaymentDialog}
                onMakeRule={handleMakeRule}
                onChangeGl={handleInlineGlChange}
                pendingGlEdits={pendingGlEdits}
                onChangeDescription={handleInlineDescriptionChange}
                onChangeCounterparty={handleInlineCounterpartyChange}
                onRequestAiSuggestion={handleRequestAiSuggestion}
                onUnlinkDocument={handleUnlinkTx}
                onOpenLinkDialog={openLinkDialogForTx}
                taxStatus={taxStatus}
                onFetchTaxStatus={fetchTaxStatus}
                isApproving={isApproving}
                onChangeProject={handleProjectChange}
                onAfterTransfer={async () => { await fetchPendingItems(pendingPage); }}
              />
            ) : (
              <div className="space-y-2">
                {/* Select All Header */}
                <div className="flex items-center gap-3 px-2 py-1 text-sm text-muted-foreground">
                  <Checkbox
                    checked={selectedItems.size === filteredPendingItems.length && filteredPendingItems.length > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-header"
                  />
                  <span>Select all {filteredPendingItems.length} transactions</span>
                </div>

                {filteredPendingItems.filter(_matchesChannelFilter).map((item, index) => (
                  <PendingTransactionCard
                    key={item.id || index}
                    item={item}
                    index={index}
                    isSelected={selectedItems.has(item.id)}
                    isApproving={isApproving === item.id}
                    isNew={isNewTransaction(item)}
                    onSelect={toggleItemSelection}
                    onQuickApprove={handleQuickApprove}
                    onReclassify={openReclassifyDialog}
                    onSplit={openSplitDialog}
                    onTaxPayment={openTaxPaymentDialog}
                    onLink={openLinkDialogForTx}
                    onUnlinkDocument={handleUnlinkTx}
                    onMakeRule={handleMakeRule}
                    onChangeGl={handleInlineGlChange}
                    pendingGlEdits={pendingGlEdits}
                    onChangeProject={handleProjectChange}
                    onSaveDescription={handleInlineDescriptionChange}
                    onSaveCounterparty={handleInlineCounterpartyChange}
                    onRequestAiSuggestion={handleRequestAiSuggestion}
                    onApplyAiSuggestion={handleApplyAiSuggestion}
                    onViewDetails={handleViewDetails}
                    formatAmount={formatAmount}
                    bankCurrency={selectedBankCurrency}
                    baseCurrency={business?.currency || 'NGN'}
                    getBankLabel={getBankLabel}
                    glAccounts={availableGlAccounts || []}
                    taxCtx={taxCtx}
                    taxStatusForItem={taxStatus[item.id]}
                    onFetchTaxStatus={fetchTaxStatus}
                    onToggleVat={handleToggleVat}
                    onToggleWht={handleToggleWht}
                    isTogglingTax={togglingTax === item.id}
                    onTaxSaved={handleAfterPendingRefresh}
                    bankAccounts={bankAccounts || []}
                    onAfterTransfer={handleAfterPendingRefresh}
                  />
                ))}
              </div>
            )}
            {/* Pending Pagination Controls - centered so the AI co-pilot bubble never covers Next */}
            {pendingTotalPages > 1 && (
              <div className="flex flex-col sm:grid sm:grid-cols-[1fr_auto_1fr] items-center gap-2 border-t pt-4 mt-4" data-testid="pending-pagination">
                <p className="text-sm text-muted-foreground sm:justify-self-start">
                  Showing {((pendingPage - 1) * PAGE_SIZE) + 1}–{Math.min(pendingPage * PAGE_SIZE, pendingTotalCount)} of {pendingTotalCount} transactions
                </p>
                <div className="flex items-center gap-2 sm:justify-self-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pendingPage <= 1}
                    onClick={() => { const p = pendingPage - 1; setPendingPage(p); fetchPendingItems(p); }}
                    data-testid="pending-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                  </Button>
                  <span className="text-sm font-medium px-2">Page {pendingPage} of {pendingTotalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pendingPage >= pendingTotalPages}
                    onClick={() => { const p = pendingPage + 1; setPendingPage(p); fetchPendingItems(p); }}
                    data-testid="pending-next-page"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="hidden sm:block" />
              </div>
            )}
            </div>
            )}
            {/* iter1107 - Awaiting Bank Post
                Bank-pending rows: informational only. Users see the charge
                right away for cash-flow awareness, but no action is possible
                until the bank posts it. When the bank posts, the row moves
                automatically to Review Needed or In Books after
                full-pipeline classification (see services/pending_gate.py). */}
            {txFilter === 'awaiting_bank' && (
              <div className="space-y-4" data-testid="awaiting-bank-content">
                <div className="bg-sky-50/60 border border-sky-200 rounded-xl p-4 flex items-start gap-3">
                  <Hourglass className="w-5 h-5 text-sky-700 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-sky-900">
                    <p className="font-semibold mb-0.5">Pending at your bank. No action needed. <InfoTip title="Pending transactions" testId="tip-pending-charges" className="ml-1">Nothing is posted to your ledger yet and amounts can still change (tips, adjustments). Once your bank confirms each one, it moves automatically to <strong>Review Needed</strong> or <strong>In Books</strong>.</InfoTip></p>
                  </div>
                </div>
                {awaitingBankItems.length === 0 ? (
                  <Card className="p-10 text-center border-dashed">
                    <Hourglass className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No transactions are pending at your bank right now.</p>
                  </Card>
                ) : (
                  <div className="space-y-2" data-testid="awaiting-bank-list">
                    {awaitingBankItems.map((tx) => (
                      <Card
                        key={tx.id}
                        className="p-4 border border-sky-100 bg-white"
                        data-testid={`awaiting-bank-row-${tx.id}`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                className="bg-sky-100 text-sky-800 border-sky-200 gap-1"
                                data-testid="awaiting-bank-badge"
                              >
                                <Hourglass className="w-3 h-3" /> Pending at bank
                              </Badge>
                              <span className="text-sm font-medium text-slate-900 truncate">
                                {tx.description || tx.counterparty || 'Bank transaction'}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {tx.date}
                              {getBankLabel && (<> · {getBankLabel(tx.bank_account_id) || 'Bank'}</>)}
                              {tx.source && <span className="ml-2 text-slate-400">via {tx.source}</span>}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className={`text-base font-semibold ${tx.direction === 'inflow' ? 'text-emerald-700' : 'text-slate-800'}`}>
                              {formatAmount ? formatAmount(tx.amount, rowCcy(tx)) : tx.amount}
                            </div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
                              Awaiting post
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
                {awaitingBankTotalCount > 0 && (
                  <p className="text-[11px] text-slate-400 pt-2">
                    {awaitingBankTotalCount} pending {awaitingBankTotalCount === 1 ? 'charge' : 'charges'} · nothing to do on this page
                  </p>
                )}
              </div>
            )}
            {txFilter === 'processed' && (
            <div className="space-y-4" data-testid="processed-content">
            {/* Phase A - Comprehensive header for Completed view */}
            <TransactionTableHeader
              mode="completed"
              transactions={filteredProcessedItems}
              currency={selectedBankCurrency || 'NGN'}
              selectedCount={0}
              totalCount={filteredProcessedItems.length}
              filters={{
                ...tableFilters,
                search: searchTerm,
                direction: filterDirection,
                bankId: filterBankAccountId,
                source: filterSource,
                glCode: filterGlCode,
                startDate: filterStartDate,
                endDate: filterEndDate,
              }}
              onFiltersChange={(next) => {
                setTableFilters(next);
                if ('search' in next) setSearchTerm(next.search || '');
                if ('direction' in next) setFilterDirection(next.direction || 'all');
                if ('bankId' in next) setFilterBankAccountId(next.bankId || 'all');
                if ('source' in next) setFilterSource(next.source || 'all');
                if ('glCode' in next) setFilterGlCode(next.glCode || 'all');
                if ('startDate' in next) setFilterStartDate(next.startDate || '');
                if ('endDate' in next) setFilterEndDate(next.endDate || '');
              }}
              bankAccounts={bankAccounts}
              glAccounts={availableGlAccounts || []}
              viewMode="table"
              showViewToggle={false}
              taxCtx={taxCtx}
              vatCandidates={processedVatCandidates}
              whtCandidates={processedWhtCandidates}
              potentialInputVat={processedPotentialInputVat}
            />
            {/* Phase G - Bulk re-classify also available for completed rows.
                Selecting completed rows surfaces the same toolbar; the bulk
                dialog will reverse + repost their journals safely. */}
            {selectedItems.size > 0 && (
              <BulkActionsToolbar
                selectedCount={selectedItems.size}
                totalCount={filteredProcessedItems.length}
                isAllSelected={false}
                isProcessing={bulkProcessing}
                onToggleSelectAll={() => {}}
                onClearSelection={clearSelection}
                onBulkReclassify={openBulkCategoryDialog}
                onBulkAttachDocs={openBulkAttachDialog}
                onBulkUndo={() => openBulkUndoDialog()}
              />
            )}
            {filteredProcessedItems.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Activity className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Processed Transactions</h3>
                  <p className="text-muted-foreground">
                    Upload a bank statement to see processed transactions here.
                  </p>
                </CardContent>
              </Card>
            ) : isPhone ? (
              <div className="space-y-2" data-testid="completed-cards-mobile">
                {filteredProcessedItems.filter(_matchesChannelFilter).map((item, index) => (
                  <CompletedTransactionCard
                    key={item.id || index}
                    item={item}
                    index={index}
                    isSelected={selectedItems.has(item.id)}
                    isNew={isNewTransaction(item)}
                    onSelect={toggleItemSelection}
                    onUndo={openUndoClassificationDialog}
                    onSplit={openSplitDialog}
                    onViewDocument={handleNavigateToDocument}
                    onViewDetails={handleViewDetails}
                    onOpenLinkDialog={openLinkDialogForTx}
                    onChangeGl={handleInlineGlChange}
                    pendingGlEdits={pendingGlEdits}
                    onChangeProject={handleProjectChange}
                    onSaveRow={handleSaveRow}
                    onSaveCounterparty={handleInlineCounterpartyChange}
                    onSaveDescription={handleInlineDescriptionChange}
                    onUnlinkDocument={handleUnlinkTx}
                    onRefresh={handleAfterProcessedRefresh}
                    formatAmount={formatAmount}
                    getBankLabel={getBankLabel}
                    getStatusBadge={getStatusBadge}
                    glAccounts={availableGlAccounts || []}
                    bankAccounts={bankAccounts || []}
                    taxCtx={taxCtx}
                    taxStatusForItem={taxStatus[item.id]}
                    onFetchTaxStatus={fetchTaxStatus}
                    onToggleVat={handleToggleVat}
                    onToggleWht={handleToggleWht}
                    isTogglingTax={togglingTax === item.id}
                    onTaxSaved={handleAfterProcessedRefresh}
                    baseCurrency={business?.currency || null}
                  />
                ))}
              </div>
            ) : (
              <TransactionTable
                mode="completed"
                transactions={filteredProcessedItems}
                currency={selectedBankCurrency || 'NGN'}
                baseCurrency={business?.currency || null}
                glAccounts={availableGlAccounts || []}
                bankAccounts={bankAccounts || []}
                taxCtx={taxCtx}
                selectedIds={selectedItems}
                onToggleSelect={toggleItemSelection}
                onToggleSelectAll={toggleSelectAll}
                onView={(tx) => { setSelectedItem(tx); setShowDetailDialog(true); }}
                onUndoClassification={openUndoClassificationDialog}
                onMakeRule={handleMakeRule}
                onSplit={openSplitDialog}
                onViewDocument={(tx) => navigateToDocument(tx.document_type, tx.document_id)}
                onViewDetails={handleViewDetails}
                onChangeGl={handleInlineGlChange}
                pendingGlEdits={pendingGlEdits}
                onSaveRow={handleSaveRow}
                onChangeDescription={handleInlineDescriptionChange}
                onChangeCounterparty={handleInlineCounterpartyChange}
                onRequestAiSuggestion={handleRequestAiSuggestion}
                onApplyAiSuggestion={handleApplyAiSuggestion}
                onUnlinkDocument={handleUnlinkTx}
                onOpenLinkDialog={openLinkDialogForTx}
                taxStatus={taxStatus}
                onFetchTaxStatus={fetchTaxStatus}
                onChangeProject={handleProjectChange}
                onAfterTransfer={async () => { await fetchProcessedItems(processedPage); }}
              />
            )}
            {/* Processed Pagination Controls */}
            {processedTotalPages > 1 && (
              <div className="flex flex-col sm:grid sm:grid-cols-[1fr_auto_1fr] items-center gap-2 border-t pt-4 mt-4" data-testid="processed-pagination">
                <p className="text-sm text-muted-foreground sm:justify-self-start">
                  Showing {((processedPage - 1) * PAGE_SIZE) + 1}–{Math.min(processedPage * PAGE_SIZE, processedTotalCount)} of {processedTotalCount} transactions
                </p>
                <div className="flex items-center gap-2 sm:justify-self-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={processedPage <= 1}
                    onClick={() => { const p = processedPage - 1; setProcessedPage(p); fetchProcessedItems(p); }}
                    data-testid="processed-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                  </Button>
                  <span className="text-sm font-medium px-2">Page {processedPage} of {processedTotalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={processedPage >= processedTotalPages}
                    onClick={() => { const p = processedPage + 1; setProcessedPage(p); fetchProcessedItems(p); }}
                    data-testid="processed-next-page"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="hidden sm:block" />
              </div>
            )}
            </div>
            )}

            {/* Duplicates Content */}
            {txFilter === 'duplicates' && (
            <div className="space-y-4" data-testid="duplicates-content">
            {duplicateItems.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Ban className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Duplicates</h3>
                  <p className="text-muted-foreground">
                    No duplicate transactions have been detected.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {duplicateItems.filter(item => {
                  const matchesBank = filterBankAccountId === 'all' || item.bank_account_id === filterBankAccountId;
                  const matchesSource = filterSource === 'all' || (filterSource === 'connected' ? ['mono', 'plaid', 'stripe', 'open_banking'].includes(item.source) : !['mono', 'plaid', 'stripe', 'open_banking'].includes(item.source));
                  const matchesSearch = !searchTerm || (item.description || '').toLowerCase().includes(searchTerm.toLowerCase());
                  return matchesBank && matchesSource && matchesSearch && _matchesChannelFilter(item);
                }).map((item, index) => (
                  <motion.div
                    key={item.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <Card className="border-l-4 border-l-gray-400">
                      <CardContent className="p-4">
                        <div className="flex flex-col gap-3">
                          {/* Transaction Info */}
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                                <Ban className="w-5 h-5 text-gray-500" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate">{item.description || 'Transaction'}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                  <span>{item.date}</span>
                                  {item.counterparty && <span>• {item.counterparty}</span>}
                                  {item.reference && <span>• Ref: {item.reference}</span>}
                                  {item.bank_account_id && getBankLabel(item.bank_account_id)}
                                  <ChannelChip item={item} />
                                </div>
                              </div>
                            </div>
                            
                            <span className={`font-semibold ${item.direction === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                              {item.direction === 'inflow' ? '+' : '-'}{formatCurrency(Math.abs(item.amount || 0), rowCcy(item))}
                            </span>
                          </div>

                          {/* Original Transaction Link */}
                          {item.original_transaction && (
                            <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm">
                                <Link2 className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-600">Duplicates:</span>
                                <span className="font-medium">{item.original_transaction.description || item.original_transaction.id}</span>
                                <span className="text-gray-500">({item.original_transaction.date})</span>
                                {item.original_transaction.matched_document_ref && (
                                  <Badge variant="outline" className="ml-2">{item.original_transaction.matched_document_ref}</Badge>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center justify-end gap-2 pt-2 border-t">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleProcessDuplicateAnyway(item.id)}
                              className="gap-1"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Not a Duplicate - Process
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDismissDuplicate(item.id)}
                              className="text-gray-500"
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
            </div>
            )}

            {/* Cross-Bank Content */}
            {txFilter === 'cross-bank' && (
            <div className="space-y-4" data-testid="cross-bank-content">
            {potentialDuplicates.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Cross-Bank Issues</h3>
                  <p className="text-muted-foreground">
                    No potential cross-bank duplicates detected. Your entries are clean.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Cross-Bank Duplicates</h3>
                    <p className="text-sm text-muted-foreground">
                      May already be in your books, possibly under another bank account. Confirm each pair.
                    </p>
                  </div>
                  <Badge variant="outline" className="border-amber-500 text-amber-600">
                    {potentialDuplicates.length} to review
                  </Badge>
                </div>

                {potentialDuplicates.map((item) => {
                  const rawMatch = item.potential_duplicate_journal;
                  const matchInfo = (rawMatch && typeof rawMatch === 'object') ? rawMatch
                    : (item.duplicate_journal_details && typeof item.duplicate_journal_details === 'object' ? item.duplicate_journal_details : {});
                  const matchedJournalGone = matchInfo.journal_status === 'voided' || matchInfo.journal_status === 'missing';
                  const SOURCE_TYPE_LABELS = {
                    payment_received: 'Invoice payment received', invoice_paid: 'Invoice payment received',
                    bill_paid: 'Bill payment', payment_made: 'Payment made', other_payment: 'Other payment',
                    other_receipt: 'Other receipt', invoice_recorded: 'Invoice recorded',
                    internal_transfer: 'Internal transfer', manual_transfer: 'Transfer', manual: 'Manual journal',
                    fixed_asset_purchase: 'Fixed asset purchase', prepayment: 'Prepayment',
                    loan_repayment: 'Loan repayment', loan_received: 'Loan received',
                    investment_made: 'Investment', payroll_settlement: 'Payroll settlement',
                    bank_charge: 'Bank charge', vendor_refund: 'Vendor refund', capital_contribution: 'Capital contribution',
                  };
                  const matchTypeLabel = SOURCE_TYPE_LABELS[matchInfo.journal_source_type] || (matchInfo.journal_source_type || '').replace(/_/g, ' ');
                  return (
                    <Card key={item.id} className="border-amber-200 bg-amber-50/30" data-testid={`cross-bank-item-${item.id}`}>
                      <CardContent className="pt-4 space-y-4">
                        {/* Two-column comparison */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Left: Bank Feed Transaction */}
                          <div className="p-3 bg-background rounded-lg border">
                            <div className="flex items-center gap-2 mb-2">
                              <ArrowDownRight className="w-4 h-4 text-blue-500" />
                              <span className="text-xs font-medium text-blue-600">Bank Feed Transaction</span>
                            </div>
                            <p className="font-medium text-sm">{item.description}</p>
                            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                              <p>Amount: <span className="font-semibold text-foreground">{formatCurrency(item.amount, rowCcy(item))}</span></p>
                              <p>Date: {item.date}</p>
                              <p>Bank: <span className="font-medium">{item.bank_name || 'Unknown'}</span></p>
                              <p>Direction: {item.direction === 'inflow' ? 'Money In' : 'Money Out'}</p>
                            </div>
                          </div>

                          {/* Right: Existing Manual Journal */}
                          <div className="p-3 bg-background rounded-lg border">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-orange-500" />
                              <span className="text-xs font-medium text-orange-600">Existing Entry in Your Books</span>
                            </div>
                            <p className="font-medium text-sm">{matchInfo.journal_description || 'Unknown'}</p>
                            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                              <p>Amount: <span className="font-semibold text-foreground">{formatCurrency(matchInfo.journal_amount, matchInfo.journal_currency || rowCcy(item))}</span></p>
                              <p>Date: {matchInfo.journal_date}</p>
                              <p>Bank: <span className="font-medium">{matchInfo.original_bank_name || 'Unknown'}</span></p>
                              <p>Type: {matchTypeLabel}</p>
                              {matchInfo.matched_document_ref && (
                                <p>Linked to: <span className="font-medium text-foreground">{matchInfo.matched_document_type === 'bill' ? 'Bill' : 'Invoice'} {matchInfo.matched_document_ref}</span>{matchInfo.matched_document_party ? `: ${matchInfo.matched_document_party}` : ''}</p>
                              )}
                              <p>Ref: {matchInfo.journal_reference}</p>
                            </div>
                            {!matchInfo.journal_id && (
                              <div className="mt-2 p-2 bg-slate-100 border rounded text-xs text-muted-foreground" data-testid={`cross-bank-nodata-${item.id}`}>
                                No match details. If you can't find it in your books, choose "Different. Process Normally".
                              </div>
                            )}
                            {matchedJournalGone && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700" data-testid={`cross-bank-stale-${item.id}`}>
                                This matched entry has since been {matchInfo.journal_status === 'voided' ? 'voided or replaced' : 'removed'}. This bank transaction is likely <strong>not</strong> a duplicate. Use "Different. Process Normally".
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Match reason */}
                        <div className="p-2 bg-amber-100/50 rounded text-xs text-amber-800">
                          <strong>Why flagged:</strong> {humanizeReviewReason(matchInfo.match_reason || item.ai_reasoning)}
                        </div>

                        {/* Resolution buttons */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => handleResolvePotentialDuplicate(item.id, 'skip')}
                            disabled={processingId === item.id}
                            data-testid={`cross-bank-skip-${item.id}`}
                          >
                            <Check className="w-3 h-3" />
                            Same Transaction · Skip Feed
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={() => handleResolvePotentialDuplicate(item.id, 'correct_bank')}
                            disabled={processingId === item.id}
                            data-testid={`cross-bank-correct-${item.id}`}
                          >
                            <ArrowLeftRight className="w-3 h-3" />
                            Same Transaction · Fix Bank
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() => handleResolvePotentialDuplicate(item.id, 'process_normally')}
                            disabled={processingId === item.id}
                            data-testid={`cross-bank-process-${item.id}`}
                          >
                            <Zap className="w-3 h-3" />
                            Different · Process Normally
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
            </div>
            )}

            {/* Failed Content */}
            {txFilter === 'failed' && (
            <div className="space-y-4" data-testid="failed-content">
            {failedItems.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">All Good!</h3>
                  <p className="text-muted-foreground">
                    No failed transactions to review.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {failedItems.filter(item => {
                  const matchesBank = filterBankAccountId === 'all' || item.bank_account_id === filterBankAccountId;
                  const matchesSource = filterSource === 'all' || (filterSource === 'connected' ? ['mono', 'plaid', 'stripe', 'open_banking'].includes(item.source) : !['mono', 'plaid', 'stripe', 'open_banking'].includes(item.source));
                  const matchesSearch = !searchTerm || (item.description || '').toLowerCase().includes(searchTerm.toLowerCase());
                  return matchesBank && matchesSource && matchesSearch;
                }).map((item, index) => (
                  <motion.div
                    key={item.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <Card className="border-l-4 border-l-red-400">
                      <CardContent className="p-4">
                        <div className="flex flex-col gap-3">
                          {/* Transaction Info */}
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <AlertCircle className="w-5 h-5 text-red-500" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate">{item.description || 'Transaction'}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                  <span>{item.date}</span>
                                  {item.counterparty && <span>• {item.counterparty}</span>}
                                  {item.reference && <span>• Ref: {item.reference}</span>}
                                  {item.bank_account_id && getBankLabel(item.bank_account_id)}
                                  <ChannelChip item={item} />
                                </div>
                              </div>
                            </div>
                            
                            <span className={`font-semibold ${item.direction === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                              {item.direction === 'inflow' ? '+' : '-'}{formatCurrency(Math.abs(item.amount || 0), rowCcy(item))}
                            </span>
                          </div>

                          {/* Error Message */}
                          <div className="bg-red-50 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-red-700">Error</p>
                                <p className="text-sm text-red-600">{item.error_message || 'Processing failed. Please try again.'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center justify-end gap-2 pt-2 border-t">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleRetryFailed(item.id)}
                              className="gap-1 bg-[#108981] hover:bg-[#0a6b65]"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Retry
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openManualProcessDialog(item)}
                              className="gap-1"
                            >
                              <Edit3 className="w-4 h-4" />
                              Process Manually
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDismissFailed(item.id)}
                              className="text-gray-500"
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
            </div>
            )}
      <BulkAttachDocsDialog
        open={bulkAttachOpen}
        onOpenChange={setBulkAttachOpen}
        entityType="bank_feed_txn"
        entityIds={selectedIdsArray}
        uploadedVia="bank_feed"
        onCompleted={() => {
          // Refresh the currently-visible list so the paperclip count updates.
          try {
            fetchPendingItems(pendingPage);
            fetchProcessedItems(processedPage);
          } catch { /* non-fatal */ }
        }}
      />
    </>
  );
};

export default BankFeedTransactionsTab;
